// Onglet « Basket » du coach (D-043, maquettes « Coach · onglet Basket » et « Séances
// types ») : Plays en vignettes (catégories, repères, recherche, « vu par »), plays
// du prochain match, bibliothèque d'exercices, séances types du club. Les joueuses
// ont le même écran Plays, en lecture, avec ce qui leur reste à voir.
import {
  addToExerciseLibrary, clubExercisesFull, clubStaffNames, createPlay, duplicateExercise, myExerciseLibrary, removeFromExerciseLibrary, currentUserId, editorUrl, matchPlays, myPlayViews, playUnseen, saveSessionPlan, sessionPlan,
  sessionTemplates, sessionTemplateUsed, teamMatches, teamPlayViews, teamPlays, visibleEvents,
  type Exercise, type Play, type PlaySide, type SessionTemplate, type Team, type TeamMatch,
} from '../lib/api';
import { agenda, addDays, localDate, parseLocalDate } from '../domain/schedule';
import { CATEGORY_LABEL, totalMinutes, type Category } from '../domain/session';
import { stepCount, stepSvg } from '../domain/court';
import { mvOpen, squadName, workTeams, type Ctx } from './context';
import { mvCoachBand, mvPlayerCard } from './mains-vives';
import { playerNoun } from '../domain/labels';
import { $, $$, html, mount, openSheet, raw, toast, type Html } from './dom';
import { emptyGuide } from '../guide/empty';
import { SIDE_LABEL, youtubeId } from './playbook';
import { imgSrc } from './play-images';
import { openNewDrill, openTeamChrono, renderDrills } from './drills';
import { deleteBtn, wireDeletes } from './supprimer';
import { isRtView, seg as seg2 } from './rt-shared';
import { renderDrillEditor } from './drill-editor';
import { makeCardFromExercise, playNowSection, renderGames, renderGamesShelf, wirePlayNow } from './games';
import { renderTeamSigns, teamSignsLink } from './team-signs';

export type BasketSeg = 'plays' | 'exercices' | 'seances' | 'drills' | 'drill' | 'signes' | 'jeu';
const SIDE_ORDER: PlaySide[] = ['attaque', 'defense', 'transition', 'remise', 'special', 'concept'];
export const CAT_COLOR: Record<Category, string> = {
  echauffement: '#E0A526', technique: '#2B5FB8', tactique: '#6D4BC2', physique: '#C2410C', jeu: '#1D7A5C', retour_calme: '#7A6E64',
};
const shortDay = (d: string) => parseLocalDate(d).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
const go = (url: string) => { location.href = url; };

/** Vignette d'un play : le départ de sa première animation, sinon l'image de sa vidéo. */
export function playThumb(p: Play): Html {
  const svg = stepSvg(p.animations[0] as never, 0, 'slice');
  if (svg) return raw(svg);
  const yt = p.videos.map(v => youtubeId(v.url)).find(Boolean);
  if (yt) return html`<img src="https://i.ytimg.com/vi/${yt}/mqdefault.jpg" alt="" loading="lazy">`;
  if (p.images[0]) { const i = imgSrc(p.images[0]); return i.aimg ? html`<img src="${i.src}" data-aimg="${i.aimg}" alt="">` : html`<img src="${p.images[0]}" alt="" loading="lazy">`; }
  return html`<span class="bk-nothumb">${p.title.slice(0, 2).toUpperCase()}</span>`;
}

/** Le prochain match de l'équipe (à venir, non annulé). */
export async function nextMatchOf(teamId: string): Promise<TeamMatch | null> {
  const now = new Date().toISOString();
  return (await teamMatches(teamId).catch(() => [] as TeamMatch[])).find(m => !m.events.cancelled_at && m.events.starts_at >= now) ?? null;
}

export async function renderBasket(root: HTMLElement, ctx: Ctx, seg: BasketSeg = 'plays', teamId?: string): Promise<void> {
  // Éditeur d'un drill (D-098) : #/basket/drill/<id | reaction | circuit | fractionne>.
  if (seg === 'drill') return renderDrillEditor(root, ctx, teamId);
  // Arceau Game (D-161, D-162) : #/basket/jeu/choix, #/basket/jeu/<neuf | id>[/cartes | /carte/<rang>].
  if (seg === 'jeu') return renderGames(root, ctx);
  // Les signes de l'équipe (D-161, D-162) : #/basket/signes/<équipe>, le staff et les joueuses et joueurs de l'équipe.
  if (seg === 'signes') return renderTeamSigns(root, ctx, teamId);
  const teams = workTeams(ctx);
  const team = teams.find(t => t.id === teamId) ?? teams[0];
  if (!team) { mount(root, html`<div class="empty"><p class="empty-title">Aucune équipe pour l'instant.</p></div>`); return; }
  const staff = ctx.staffOf.has(team.id);
  if (!staff) seg = 'plays';
  mount(root, html`
    <section class="screen bk">
      <header class="screen-head bk-head">
        <div><p class="eyebrow">${team.name}</p><h2>${staff || mvOpen(ctx, team.club_id) ? 'Basket' : 'Plays'}</h2></div>
        ${staff && seg === 'plays' ? html`<button type="button" class="btn btn-primary btn-sm" id="b-new-play" data-guide="bk-new-play">+ Play</button>` : ''}
        ${staff && seg === 'drills' ? html`<button type="button" class="btn btn-primary btn-sm" id="b-new-drill" data-guide="dr-new">+ Drill</button>` : ''}
      </header>
      ${teams.length > 1 ? html`<nav class="team-strip" aria-label="Équipes" data-replace>${teams.map(t => html`<a class="team-pill ${t.id === team.id ? 'on' : ''}" href="#/basket/${seg}/${t.id}">${t.name}</a>`)}</nav>` : ''}
      ${staff ? html`<nav class="seg bk-seg bk-seg5" aria-label="Basket" data-guide="bk-seg">
        <a href="#/basket/plays/${team.id}" ${seg === 'plays' ? html`aria-current="page"` : ''}>Plays</a>
        <a href="#/basket/exercices/${team.id}" ${seg === 'exercices' ? html`aria-current="page"` : ''}>Exercices</a>
        <a href="#/basket/seances/${team.id}" ${seg === 'seances' ? html`aria-current="page"` : ''}>Séances types</a>
        <a href="#/basket/drills/${team.id}" ${seg === 'drills' ? html`aria-current="page"` : ''} data-guide="bk-drills">Drills et jeux</a>
      </nav>` : ''}
      ${staff && seg === 'exercices' && isRtView(ctx) ? seg2([['Exercices', `#/basket/exercices/${team.id}`], ['Drills du club', '#/rt/projet/drills']], 0) : ''}
      <div id="bk-body" class="screen"></div>
    </section>`);
  const body = $(root, '#bk-body')!;
  $(root, '#b-new-drill')?.addEventListener('click', openNewDrill);
  $(root, '#b-new-play')?.addEventListener('click', () => openNewPlay(team));
  if (seg === 'drills') return renderDrillsTab(body, ctx, team);
  if (seg === 'exercices') return renderExercises(body, team, ctx.uid, ctx.adminOf.some(c => c.id === team.club_id) || ctx.roles.some(r => r.role === 'technical' && r.club_id === team.club_id), 'mine', ctx);
  if (seg === 'seances') return renderTemplates(body, ctx, team);
  return renderPlays(body, ctx, team, staff);
}

// -----------------------------------------------------------------------------
// Drills et jeux, remis en ordre (D-167) : lisible d'un coup d'œil. Jouer maintenant (Le Parcours, Le Sablier, le chrono),
// mes drills, mes jeux et mes paquets, ceux du club (repliés), Mains vives, les signes de l'équipe.
// -----------------------------------------------------------------------------
async function renderDrillsTab(body: HTMLElement, ctx: Ctx, team: Team): Promise<void> {
  const signs = await teamSignsLink(ctx, team);
  const mv = mvCoachBand(ctx, team);
  mount(body, html`<div class="jx">
    ${playNowSection(team)}
    <section class="jx-sec" data-guide="dr-views"><div class="jx-h"><h3>Mes drills</h3><small>ta bibliothèque</small></div><div id="jx-drills"></div></section>
    <div id="jx-games"></div>
    <details class="jx-club" id="jx-club"><summary>Ceux du club</summary><div id="jx-club-drills"></div><div id="jx-club-games"></div></details>
    ${mv ? html`<section class="jx-sec"><div class="jx-h"><h3>Mains vives</h3><small>le jeu caméra, chez soi</small></div>${mv}</section>` : ''}
    ${signs ? html`<section class="jx-sec"><div class="jx-h"><h3>Les signes de l’équipe</h3><small>les gestes du meneur</small></div>${signs}</section>` : ''}
  </div>`);
  wirePlayNow(body, team);
  $(body, '#dr-chrono')?.addEventListener('click', () => openTeamChrono(team));
  const [, , nd, ng] = await Promise.all([
    renderDrills($(body, '#jx-drills')!, ctx, team, 'mine', true), renderGamesShelf($(body, '#jx-games')!, ctx, team, 'mine'),
    renderDrills($(body, '#jx-club-drills')!, ctx, team, 'club', true), renderGamesShelf($(body, '#jx-club-games')!, ctx, team, 'club'),
  ]);
  const sum = $(body, '#jx-club summary');
  if (sum) sum.textContent = nd + ng ? `Ceux du club (${nd + ng})` : 'Ceux du club : rien de plus pour l’instant';
}

// -----------------------------------------------------------------------------
// Plays
// -----------------------------------------------------------------------------
async function renderPlays(el: HTMLElement, ctx: Ctx, team: Team, staff: boolean) {
  // D-164 : côté joueur, la carte « Mains vives » en tête (seulement là où le jeu est ouvert).
  const [plays, next, signsLink, hero] = await Promise.all([teamPlays(team.id), nextMatchOf(team.id), teamSignsLink(ctx, team), staff ? Promise.resolve('' as const) : mvPlayerCard(ctx, team)]);
  const [views, mine, mPlays] = await Promise.all([
    staff ? teamPlayViews(team.id).catch(() => []) : Promise.resolve([]),
    staff ? Promise.resolve([]) : myPlayViews(plays.map(p => p.id)).catch(() => []),
    next ? matchPlays(next.event_id).catch(() => []) : Promise.resolve([]),
  ]);
  const viewOf = new Map(views.map(v => [v.play_id, v]));
  const seen = new Set(mine.map(v => v.play_id));
  // Un repère peut en contenir plusieurs (« HORNS, 32 ») : chaque repère est un filtre.
  const tagsOf = (p: Play) => (p.tag ?? '').split(',').map(t => t.trim().toUpperCase()).filter(Boolean);
  const tags = [...new Set(plays.flatMap(tagsOf))].sort((a, b) => a.localeCompare(b));
  const sides = SIDE_ORDER.filter(s => plays.some(p => p.side === s));
  const ordered = [...plays].sort((a, b) => SIDE_ORDER.indexOf(a.side) - SIDE_ORDER.indexOf(b.side) || a.title.localeCompare(b.title));
  const matchList = mPlays.map(m => ({ m, p: plays.find(p => p.id === m.play_id) })).filter((x): x is { m: typeof x.m; p: Play } => !!x.p);
  // « Ont tout vu » : l'effectif moins celles à qui il manque au moins un play du match.
  let allSeen: { n: number; of: number } | null = null;
  if (staff && matchList.length) {
    const unseen = await Promise.all(matchList.map(x => playUnseen(x.p.id).catch(() => [])));
    const missing = new Set(unseen.flat().map(u => u.person_id));
    const roster = viewOf.get(matchList[0]!.p.id)?.roster ?? 0;
    allSeen = { n: Math.max(0, roster - missing.size), of: roster };
  }
  const nextLabel = next ? `${squadName(ctx, next.squad_id) ?? team.name} ${next.is_home === false ? 'à' : '–'} ${next.opponent}` : '';

  const tile = (p: Play) => {
    const v = viewOf.get(p.id);
    const pill = staff
      ? (p.published ? html`<span class="bk-pill ${v && v.roster && v.seen >= v.roster ? 'ok' : ''}">${v ? `${v.seen}/${v.roster} vues` : 'visible'}</span>` : html`<span class="bk-pill mute">Coachs</span>`)
      : (seen.has(p.id) ? '' : html`<span class="bk-pill new">À voir</span>`);
    const n = stepCount(p.animations[0] as never);
    return html`
      <a class="bk-tile" data-guide="bk-play" href="#/play/${p.id}" data-side="${p.side}" data-tags="${tagsOf(p).join('|')}" data-q="${`${p.title} ${p.tag ?? ''} ${SIDE_LABEL[p.side] ?? ''}`.toLowerCase()}">
        <span class="bk-thumb">${playThumb(p)}${n ? html`<span class="bk-steps">${n} ${n > 1 ? 'TEMPS' : 'TEMPS'}</span>` : ''}${pill}</span>
        <span class="bk-cap"><strong>${p.title}</strong>
          <small>${[SIDE_LABEL[p.side], p.tag, p.videos.length ? `${p.videos.length} vidéo${p.videos.length > 1 ? 's' : ''}` : ''].filter(Boolean).join(' · ')}</small></span>
      </a>`;
  };

  mount(el, html`
    ${hero}
    ${plays.length > 4 ? html`<label class="bk-search"><span class="ti ti-search" aria-hidden="true"></span>
      <input type="search" id="bk-q" placeholder="Chercher un play, un repère (horns, zone…)" aria-label="Chercher un play"></label>` : ''}
    ${sides.length > 1 ? html`<div class="chip-scroll" role="group" aria-label="Catégories">
      <button type="button" class="chip-toggle" data-side-f="" aria-pressed="true">Tous ${plays.length}</button>
      ${sides.map(s => html`<button type="button" class="chip-toggle" data-side-f="${s}" aria-pressed="false">${SIDE_LABEL[s]} ${plays.filter(p => p.side === s).length}</button>`)}
    </div>` : ''}
    ${tags.length > 1 ? html`<div class="chip-scroll" role="group" aria-label="Repères">${tags.map(t => html`<button type="button" class="chip-toggle chip-sm" data-tag-f="${t}" aria-pressed="false">${t}</button>`)}</div>` : ''}
    ${next && (matchList.length || staff) ? html`
      <section class="bk-match">
        <div class="bk-match-top"><span class="eyebrow">Plays du match · ${shortDay(localDate(new Date(next.events.starts_at)))}</span>
          <span class="small">${matchList.length} ${matchList.length > 1 ? 'plays' : 'play'}</span></div>
        <strong class="bk-match-title">${nextLabel}</strong>
        ${matchList.length ? html`<span class="bk-match-chips">${matchList.slice(0, 4).map((x, i) => html`<a href="#/play/${x.p.id}">${i + 1}. ${x.p.title}</a>`)}${matchList.length > 4 ? html`<span>+${matchList.length - 4}</span>` : ''}</span>`
          : html`<span class="small">Ouvre un play et touche « Au match » pour préparer ce match.</span>`}
        ${allSeen && allSeen.of ? html`<span class="bk-match-seen"><span class="bk-bar"><span style="width:${Math.round((allSeen.n / allSeen.of) * 100)}%"></span></span><b>${allSeen.n} / ${allSeen.of} ont tout vu</b></span>` : ''}
      </section>` : ''}
    ${hero && plays.length ? html`<div class="mv-sec"><h3>Les plays</h3><span class="muted small">${plays.length}${plays.length - seen.size > 0 ? ` · ${plays.length - seen.size} à voir` : ''}</span></div>` : ''}
    ${signsLink}
    ${plays.length ? html`<div class="bk-grid">${ordered.map(tile)}</div><p class="muted small bk-none" hidden>Aucun play ne correspond.</p>`
      : staff ? emptyGuide({ art: 'court', guide: 'bk-empty', title: 'Ton premier play en 2 minutes', tour: 'creer-play',
          text: `Place tes ${playerNoun(team.gender, 2)}, dessine les déplacements : l’équipe le revoit en animation, séquence par séquence.`,
          alt: { label: 'Le tuto pas à pas', href: '#/aide/creer-play' }, foot: 'Positions, courses, écrans, passes : tout s’anime, temps par temps.' })
        : hero ? '' : emptyGuide({ art: 'court', guide: 'bk-empty', title: 'Pas encore de play à revoir', text: 'Ton coach les montrera ici : tu les revois en animation, avant le match.' })}`);

  // Filtres : catégorie, repères, recherche (cumulés).
  let side = '';
  const onTags = new Set<string>();
  const filter = () => {
    const q = ($<HTMLInputElement>(el, '#bk-q')?.value ?? '').trim().toLowerCase();
    let any = false;
    for (const t of $$<HTMLAnchorElement>(el, '.bk-tile')) {
      const has = (t.dataset.tags ?? '').split('|');
      const hit = (!side || t.dataset.side === side) && (!onTags.size || has.some(x => onTags.has(x))) && (!q || t.dataset.q!.includes(q));
      t.hidden = !hit; any ||= hit;
    }
    const none = $(el, '.bk-none'); if (none) none.hidden = any;
  };
  $<HTMLInputElement>(el, '#bk-q')?.addEventListener('input', filter);
  for (const b of $$<HTMLButtonElement>(el, '[data-side-f]')) b.addEventListener('click', () => {
    side = b.dataset.sideF ?? '';
    $$(el, '[data-side-f]').forEach(x => x.setAttribute('aria-pressed', String(x === b)));
    filter();
  });
  for (const b of $$<HTMLButtonElement>(el, '[data-tag-f]')) b.addEventListener('click', () => {
    const t = b.dataset.tagF!;
    if (onTags.has(t)) onTags.delete(t); else onTags.add(t);
    b.setAttribute('aria-pressed', String(onTags.has(t)));
    filter();
  });
}

/** « + Play » : un nom, une catégorie, un repère, puis l'éditeur. Branché dès le premier rendu de l'en-tête (renderBasket),
 *  pas après le chargement des plays : le bouton est là tout de suite, son geste aussi (et la visite « creer-play » le montre). */
function openNewPlay(team: Team): void {
  openSheet('Nouveau play', html`
    <form class="form" id="f-play" novalidate>
      <label class="field"><span>Nom</span><input name="title" required maxlength="80" placeholder="ex. Horns flare, Remise côté"></label>
      <div class="row-2">
        <label class="field"><span>Catégorie</span><select name="side">${SIDE_ORDER.map(s => html`<option value="${s}">${SIDE_LABEL[s]}</option>`)}</select></label>
        <label class="field"><span>Repère (facultatif)</span><input name="tag" maxlength="40" placeholder="ex. HORNS, ZONE"></label>
      </div>
      <p class="form-msg" role="alert"></p>
      <button class="btn btn-primary" type="submit">Créer et dessiner</button>
    </form>`, (sheet) => {
    const f = $<HTMLFormElement>(sheet, '#f-play')!;
    f.addEventListener('submit', async (e) => {
      e.preventDefault();
      const d = new FormData(f);
      const title = String(d.get('title') ?? '').trim();
      if (!title) { $(f, '.form-msg')!.textContent = 'Donne un nom au play.'; return; }
      try {
        const p = await createPlay({ club_id: team.club_id, team_id: team.id, title, side: String(d.get('side')) as PlaySide, tag: String(d.get('tag') ?? '').trim() || null });
        go(editorUrl({ play: p.id }));
      } catch { $(f, '.form-msg')!.textContent = 'Création impossible. Réessaie.'; }
    });
  });
}

// -----------------------------------------------------------------------------
// Exercices (bibliothèque du club)
// -----------------------------------------------------------------------------
/** Exercices (D-063) : « Ma bibliothèque » (les miens et ceux que j'ai ajoutés) par défaut,
 *  « Ceux du club » pour parcourir ceux des autres coachs et les ajouter. */
async function renderExercises(el: HTMLElement, team: Team, uid: string, admin: boolean, view: 'mine' | 'club' = 'mine', ctx?: Ctx) {
  const [all, lib, names] = await Promise.all([
    clubExercisesFull(team.club_id).catch(() => [] as (Exercise & { diagram: unknown })[]), myExerciseLibrary().catch(() => new Set<string>()),
    clubStaffNames(team.club_id).catch(() => new Map<string, string>()),
  ]);
  const isMine = (x: Exercise) => x.created_by === uid || lib.has(x.id);
  const mineList = all.filter(isMine), clubList = all.filter(x => !isMine(x));
  const list = view === 'mine' ? mineList : clubList;
  const author = (x: Exercise) => (!x.created_by ? 'Du club' : x.created_by === uid ? 'Toi' : names.get(x.created_by) ?? 'Un coach');
  const cats = (Object.keys(CATEGORY_LABEL) as Category[]).filter(c => list.some(x => x.category === c));
  const reload = (v = view) => renderExercises(el, team, uid, admin, v, ctx);
  mount(el, html`
    <div class="seg ex-views" role="tablist" aria-label="Bibliothèque">
      <button type="button" role="tab" data-view="mine" aria-selected="${String(view === 'mine')}">Ma bibliothèque (${mineList.length})</button>
      <button type="button" role="tab" data-view="club" aria-selected="${String(view === 'club')}">Ceux du club (${clubList.length})</button>
    </div>
    ${list.length > 4 ? html`<label class="bk-search"><span class="ti ti-search" aria-hidden="true"></span>
      <input type="search" id="ex-q" placeholder="Chercher un exercice" aria-label="Chercher un exercice"></label>` : ''}
    ${cats.length > 1 ? html`<div class="chip-scroll" role="group" aria-label="Catégories">
      <button type="button" class="chip-toggle" data-cat="" aria-pressed="true">Tous ${list.length}</button>
      ${cats.map(c => html`<button type="button" class="chip-toggle" data-cat="${c}" aria-pressed="false">${CATEGORY_LABEL[c]}</button>`)}</div>` : ''}
    ${list.length ? html`<div class="bk-grid">${list.map(x => {
      const svg = stepSvg(x.diagram as never, 0, 'slice');
      return html`<div class="bk-tile-wrap"><button type="button" class="bk-tile" data-ex="${x.id}" data-cat-of="${x.category}" data-q="${`${x.title} ${CATEGORY_LABEL[x.category]} ${author(x)}`.toLowerCase()}">
        <span class="bk-thumb" style="--cat:${CAT_COLOR[x.category]}">${svg ? raw(svg) : html`<span class="bk-nothumb">${CATEGORY_LABEL[x.category]}</span>`}</span>
        <span class="bk-cap"><strong>${x.title}</strong><small>${CATEGORY_LABEL[x.category]}${x.minutes ? ` · ${x.minutes} min` : ''} · ${author(x)}</small></span>
      </button>
      ${view === 'club' ? html`<button type="button" class="ex-add" data-add="${x.id}" aria-label="Ajouter ${x.title} à ma bibliothèque">+ Ma bibliothèque</button>` : ''}</div>`;
    })}</div>` : view === 'mine'
      ? html`<div class="empty"><p class="empty-title">Ta bibliothèque est vide</p><p>Crée tes exercices depuis une séance (« + Exercice »), ou reprends ceux des autres coachs dans « Ceux du club ».</p></div>`
      : html`<div class="empty"><p class="empty-title">Rien de plus au club</p><p>Les exercices des autres coachs apparaîtront ici.</p></div>`}`);
  let cat = '';
  const filter = () => {
    const q = ($<HTMLInputElement>(el, '#ex-q')?.value ?? '').trim().toLowerCase();
    for (const t of $$<HTMLElement>(el, '.bk-tile')) t.parentElement!.hidden = (!!cat && t.dataset.catOf !== cat) || (!!q && !t.dataset.q!.includes(q));
  };
  $<HTMLInputElement>(el, '#ex-q')?.addEventListener('input', filter);
  for (const b of $$<HTMLButtonElement>(el, '[data-view]')) b.addEventListener('click', () => void reload(b.dataset.view === 'club' ? 'club' : 'mine'));
  for (const b of $$<HTMLButtonElement>(el, '[data-cat]')) b.addEventListener('click', () => {
    cat = b.dataset.cat ?? ''; $$(el, '[data-cat]').forEach(x => x.setAttribute('aria-pressed', String(x === b))); filter();
  });
  for (const b of $$<HTMLButtonElement>(el, '[data-add]')) b.addEventListener('click', async () => {
    try { await addToExerciseLibrary(b.dataset.add!); toast('Ajouté à ta bibliothèque'); await reload(); } catch { toast('Impossible.', 'ko'); }
  });
  for (const b of $$<HTMLButtonElement>(el, '[data-ex]')) b.addEventListener('click', () => {
    const x = all.find(e => e.id === b.dataset.ex)!;
    const svg = stepSvg(x.diagram as never);
    const canEdit = !x.created_by || x.created_by === uid || admin;
    openSheet(x.title, html`
      ${svg ? html`<div class="bk-big">${raw(svg)}</div>` : ''}
      <p class="muted">${CATEGORY_LABEL[x.category]}${x.minutes ? ` · ${x.minutes} min` : ''} · ${x.created_by === uid ? 'de toi' : x.created_by ? `de ${author(x)}` : 'exercice du club'}</p>
      ${x.description ? html`<p class="pb-desc">${x.description}</p>` : ''}
      ${canEdit ? html`<a class="btn btn-primary" href="${editorUrl({ exo: x.id })}">${x.diagram ? 'Voir / modifier le schéma' : 'Dessiner le schéma'}</a>`
        : html`${x.diagram ? html`<a class="btn btn-ghost" href="${editorUrl({ exo: x.id, view: true })}">Voir le schéma animé</a>` : ''}
          <button type="button" class="btn btn-ghost" id="ex-dup">Dupliquer pour l'adapter</button>`}
      ${ctx ? html`<button type="button" class="btn btn-ghost" id="ex-card">En faire une carte</button>` : ''}
      ${x.created_by && x.created_by !== uid ? (lib.has(x.id)
        ? html`<button type="button" class="btn btn-link" id="ex-out">Retirer de ma bibliothèque</button>`
        : html`<button type="button" class="btn btn-primary" id="ex-in">Ajouter à ma bibliothèque</button>`) : ''}
      <p class="hint">Pour l'ajouter à une séance : ouvre la séance, puis « + Exercice ».</p>
      ${x.created_by === uid || admin ? deleteBtn('exercise', x.id, 'Supprimer de la bibliothèque') : ''}`, (sheet, close) => {
      wireDeletes(sheet, () => { close(); void reload(); });   // D-178 : l'auteur, le bureau ou le responsable technique
      $(sheet, '#ex-in')?.addEventListener('click', async () => { try { await addToExerciseLibrary(x.id); close(); toast('Ajouté à ta bibliothèque'); await reload(); } catch { toast('Impossible.', 'ko'); } });
      $(sheet, '#ex-out')?.addEventListener('click', async () => { try { await removeFromExerciseLibrary(x.id); close(); toast('Retiré de ta bibliothèque'); await reload(); } catch { toast('Impossible.', 'ko'); } });
      $(sheet, '#ex-card')?.addEventListener('click', () => { close(); if (ctx) void makeCardFromExercise(ctx, team, x); });
      $(sheet, '#ex-dup')?.addEventListener('click', async () => {
        try { const { id } = await duplicateExercise(x); close(); toast('Copie créée : elle est à toi'); location.href = editorUrl({ exo: id }); }
        catch { toast('Copie impossible.', 'ko'); }
      });
    });
  });
}

// -----------------------------------------------------------------------------
// Séances types (modèles du club)
// -----------------------------------------------------------------------------
async function renderTemplates(el: HTMLElement, ctx: Ctx, team: Team) {
  const [list, me, events] = await Promise.all([
    sessionTemplates(team.club_id).catch(() => [] as SessionTemplate[]), currentUserId(), visibleEvents().catch(() => []),
  ]);
  const today = localDate(new Date());
  const nextTraining = agenda(events.filter(e => e.team_id === team.id && e.kind === 'training' && !e.cancelled_at), today, addDays(today, 30))[0] ?? null;
  const levels = [...new Set(list.map(t => t.level).filter((l): l is string => !!l))].sort();
  const bar = (t: SessionTemplate) => {
    const total = totalMinutes(t.items) || 1;
    return html`<span class="tpl-bar" aria-hidden="true">${t.items.map(i => html`<span style="flex:${Math.max(1, i.minutes || 0) / total};background:${CAT_COLOR[i.category] ?? '#7A6E64'}"></span>`)}</span>`;
  };
  mount(el, html`
    ${levels.length > 1 ? html`<div class="chip-scroll" role="group" aria-label="Public">
      <button type="button" class="chip-toggle" data-lv="" aria-pressed="true">Tout le club ${list.length}</button>
      ${levels.map(l => html`<button type="button" class="chip-toggle" data-lv="${l}" aria-pressed="false">${l}</button>`)}</div>` : ''}
    ${list.length ? list.map(t => html`
      <section class="block tpl-card" data-lv-of="${t.level ?? ''}">
        <div class="tpl-top"><span class="eyebrow eyebrow-accent">${[t.level, `${t.items.length} exo${t.items.length > 1 ? 's' : ''}`].filter(Boolean).join(' · ')}</span>
          <b class="tpl-min">${totalMinutes(t.items)}'</b></div>
        <h3>${t.title}</h3>
        ${t.theme ? html`<p class="small">${t.theme}</p>` : ''}
        ${bar(t)}
        <div class="tpl-foot"><span class="muted small">${t.created_by === me ? 'Par toi' : 'Coach du club'}${t.uses ? ` · utilisée ${t.uses} fois` : ''}</span>
          ${nextTraining ? html`<button type="button" class="btn btn-ghost btn-sm" data-use="${t.id}">Utiliser ${shortDay(nextTraining.date)}</button>` : ''}</div>
      </section>`)
      : html`<div class="empty"><p class="empty-title">Pas encore de séance type</p><p>Compose une séance, puis « Enregistrer comme séance type » : tous les coachs du club la retrouvent ici.</p></div>`}
    <div class="cat-legend">${(Object.keys(CATEGORY_LABEL) as Category[]).map(c => html`<span><i style="background:${CAT_COLOR[c]}"></i>${CATEGORY_LABEL[c]}</span>`)}</div>`);
  for (const b of $$<HTMLButtonElement>(el, '[data-lv]')) b.addEventListener('click', () => {
    const lv = b.dataset.lv ?? '';
    $$(el, '[data-lv]').forEach(x => x.setAttribute('aria-pressed', String(x === b)));
    for (const c of $$<HTMLElement>(el, '.tpl-card')) c.hidden = !!lv && c.dataset.lvOf !== lv;
  });
  for (const b of $$<HTMLButtonElement>(el, '[data-use]')) b.addEventListener('click', async () => {
    const t = list.find(x => x.id === b.dataset.use)!;
    if (!nextTraining) return;
    const ev = nextTraining.eventId, date = nextTraining.date;
    const plan = (await sessionPlan(ev, date).catch(() => null)) ?? { event_id: ev, occurrence_date: date, items: [], coach_notes: null, published: false };
    const apply = async (mode: 'replace' | 'append') => {
      const items = t.items.map(x => ({ ...x, id: crypto.randomUUID().slice(0, 8) }));
      try {
        await saveSessionPlan({ ...plan, items: mode === 'append' ? [...plan.items, ...items] : items, theme: plan.theme || t.theme, coach_notes: plan.coach_notes || t.coach_notes });
        void sessionTemplateUsed(t.id);
        location.hash = `#/seance/${ev}/${date}`;
      } catch { toast('Impossible pour le moment.', 'ko'); }
    };
    if (!plan.items.length) { await apply('replace'); return; }
    openSheet('La séance a déjà un plan', html`
      <p>${plan.items.length} exercice${plan.items.length > 1 ? 's' : ''} déjà prévus le ${shortDay(date)}.</p>
      <div class="btn-row"><button type="button" class="btn btn-primary" id="b-rep">Remplacer</button>
        <button type="button" class="btn btn-ghost" id="b-app">Ajouter à la suite</button></div>`, (sheet, close) => {
      $(sheet, '#b-rep')!.addEventListener('click', () => { close(); void apply('replace'); });
      $(sheet, '#b-app')!.addEventListener('click', () => { close(); void apply('append'); });
    });
  });
}
